import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { LegacyPageConfig } from "./legacyPageRegistryTypes";

interface UseLegacyPageRoutingResult {
  currentPageName: string;
  pageConfig?: LegacyPageConfig;
  PageComponent?: LegacyPageConfig["component"];
  isValidPage: boolean;
  navigateToPageName: (pageName: string) => void;
}

interface CreateUseLegacyPageRoutingParams {
  getPageConfig: (pageName: string) => LegacyPageConfig | undefined;
  isPageRegistered: (pageName: string) => boolean;
  defaultPageName: string;
  /**
   * Param do legado: `/:id/*` — `id` é o pageName; o splat fica para a página.
   */
  paramName?: string;
}

/**
 * Fábrica de hook para conectar um registro de páginas do legado ao React Router.
 *
 * Padrão espelhado do `src-next/shared/routing/createUsePageRouting.ts`, mas:
 * - com nomenclatura explícita (`pageName`) para evitar confusão com `path`.
 * - compatível com o param atual do legado (`id`).
 */
export function createUseLegacyPageRouting(params: CreateUseLegacyPageRoutingParams) {
  const {
    getPageConfig,
    isPageRegistered,
    defaultPageName,
    paramName = "id",
  } = params;

  return function useLegacyPageRouting(): UseLegacyPageRoutingResult {
    const routeParams = useParams();
    const navigate = useNavigate();

    const currentPageName = useMemo(() => {
      const value = routeParams[paramName] as string | undefined;
      if (!value) {
        return defaultPageName;
      }

      return value;
    }, [defaultPageName, paramName, routeParams]);

    const pageConfig = useMemo(() => {
      return getPageConfig(currentPageName);
    }, [currentPageName, getPageConfig]);

    const PageComponent = pageConfig?.component;

    const isValidPage = useMemo(() => {
      return isPageRegistered(currentPageName);
    }, [currentPageName, isPageRegistered]);

    const navigateToPageName = (pageName: string) => {
      if (pageName === defaultPageName) {
        // Mantém a compatibilidade do legado: rota index renderiza home sem mudar URL.
        navigate("/");
        return;
      }

      navigate(`/${pageName}`);
    };

    return {
      currentPageName,
      pageConfig,
      PageComponent,
      isValidPage,
      navigateToPageName,
    };
  };
}

